import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "react-toastify";
import { updateOrganizerPassword } from "@/store/slices/organizerSlice";
import Button from "@/components/common/Button";
import TextInput from "@/components/common/TextInput";

const SettingsSecurity = () => {
  const dispatch = useDispatch();
  const { isLoading } = useSelector((state) => state.organizer);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm();

  const newPassword = watch("new_password");

  const onSubmitPassword = async (data) => {
    try {
      const { confirm_password, ...passwordData } = data;
      await dispatch(updateOrganizerPassword(passwordData)).unwrap();
      toast.success("Password updated successfully");
      reset();
    } catch (error) {
      toast.error(error?.message || "Failed to update password");
    }
  };

  return (
    <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl"
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
        <div className="mb-6 pb-6 border-b border-gray-100 dark:border-gray-700">
             <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                <Lock className="w-5 h-5 text-amber-600" />
                Change Password
             </h2>
             <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                 Ensure your account is using a long, random password to stay secure.
             </p>
        </div>

        <form onSubmit={handleSubmit(onSubmitPassword)} className="space-y-4">
          <div className="relative">
            <TextInput
              label="Current Password"
              type={showCurrentPassword ? "text" : "password"}
              placeholder="Enter current password"
              error={errors.current_password?.message}
              {...register("current_password", {
                required: "Current password is required",
              })}
            />
            <button
              type="button"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
            >
              {showCurrentPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>

          <div className="relative">
            <TextInput
              label="New Password"
              type={showNewPassword ? "text" : "password"}
              placeholder="Enter new password"
              error={errors.new_password?.message}
              {...register("new_password", {
                required: "New password is required",
                minLength: {
                  value: 6,
                  message: "Password must be at least 6 characters",
                },
              })}
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
            >
              {showNewPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>

          <TextInput
            label="Confirm New Password"
            type="password"
            placeholder="Confirm new password"
            error={errors.confirm_password?.message}
            {...register("confirm_password", {
              required: "Please confirm your password",
              validate: (value) =>
                value === newPassword || "Passwords do not match",
            })}
          />

          <div className="pt-4 flex justify-end">
             <Button
                type="submit"
                loading={isLoading}
                className="bg-amber-600 hover:bg-amber-700 text-white px-8"
              >
                Update Password
              </Button>
          </div>
        </form>
      </div>
    </motion.div>
  );
};

export default SettingsSecurity;
